import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Building2, Plus, FileText, Info, Ban, RotateCcw } from 'lucide-react';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  PageHead, Panel, Register, FigureStrip, Figure, DetailDrawer, AcStatus, Currency, Field, FormRow, fmtDate, money,
} from './design';

const SERVICE_TYPES = [
  'Personal Care',
  'Domestic Assistance',
  'Social Support',
  'Nursing',
  'Allied Health',
  'Transport',
  'Home Maintenance',
  'Meals',
  'Respite',
  'Equipment / Aids',
];

const schemeLabel = (scheme) => {
  const s = String(scheme || '').toUpperCase();
  if (s === 'HCP') return 'Home Care Package';
  if (s === 'SAH') return 'Support at Home';
  return null;
};

const ymd = (d) => (d ? String(d).slice(0, 10) : '');

// Production sends either camelCase or the column name, and sometimes a server-computed balance.
const allocOf = (o) => Number(o.allocated ?? o.allocated_amount ?? 0);
const usedOf = (o) => Number(o.utilised ?? o.utilised_amount ?? 0);
const balanceOf = (o) => {
  const bal = o.balance ?? o.balance_amount;
  return bal != null ? Number(bal) : allocOf(o) - usedOf(o);
};

// A purchase order is open ('active'), closed or cancelled; one saved before statuses were set is
// open (the server matches invoices the same way). Said in words, never as the stored value.
const PO_STATUS = { active: 'Open', closed: 'Closed', cancelled: 'Cancelled' };
const poStatus = (o) => {
  const s = String((o && o.status) || 'active').toLowerCase();
  return PO_STATUS[s] ? s : 'active';
};
const isOpenPO = (o) => poStatus(o) === 'active';

// A provider stopped with "Stop using" stays on the list (its orders and invoices still point at
// it), but is no longer offered for a new purchase order.
const inUse = (p) => p.is_active !== false;

// A purchase order is a cap. Once spend crosses four fifths of it the coordinator needs to know
// before the next invoice arrives; past the cap the order can no longer authorise anything.
const poTone = (o) => {
  const alloc = allocOf(o);
  const used = usedOf(o);
  if (used > alloc) return 'rose';
  if (alloc > 0 && used > alloc * 0.8) return 'amber';
  return null;
};

// Row actions sit inside a clickable row: without this a click on one would also open the record,
// and Enter on the button would be taken by the row.
const stop = (e) => e.stopPropagation();

// The section's form shell: the same side drawer as every other aged care form, so adding a provider
// and raising a purchase order keep the list in view behind them. The drawer handles Escape, holds
// focus while open and returns it to the button that opened it.
function FormDrawer({ title, icon, purpose, onClose, onSave, saving, saveLabel, children }) {
  return (
    <DetailDrawer
      open
      onClose={onClose}
      icon={icon}
      width="md"
      title={title}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="button" onClick={onSave} disabled={saving} className="btn-primary">
            {saving ? 'Saving…' : saveLabel}
          </button>
        </>
      )}
    >
      {/* In the body rather than the header, where a subtitle is cut to one line. */}
      {purpose && <p className="text-[13px] leading-relaxed text-slate-500 mb-4">{purpose}</p>}
      {children}
    </DetailDrawer>
  );
}

// Add a provider, or edit one (`provider` set). Bank details let approved invoices be paid by bank
// file from Supplier invoices, with a remittance email to the address given.
function ProviderModal({ provider, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: provider?.name || '', abn: provider?.abn || '', contactName: provider?.contact_name || '',
    email: provider?.email || '', phone: provider?.phone || '',
    bsb: provider?.bsb || '', accountNumber: provider?.account_number || '', accountName: provider?.account_name || '',
    remittanceEmail: provider?.remittance_email || '',
    screeningAttestedAt: provider?.screening_attested_at ? String(provider.screening_attested_at).slice(0, 10) : '',
    screeningExpires: provider?.screening_expires ? String(provider.screening_expires).slice(0, 10) : '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) return toast.error('Enter the provider name before saving');
    setSaving(true);
    try {
      if (provider) await api.patch(`/aged-care/providers/${provider.id}`, form);
      else await api.post('/aged-care/providers', form);
      toast.success(provider ? 'Provider saved' : 'Provider added');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.message || (provider ? 'Could not save the provider' : 'Could not add the provider'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDrawer
      icon={Building2}
      title={provider ? `Edit ${provider.name}` : 'Add a provider'}
      purpose="A provider you broker services from. Once it is here you can raise purchase orders against it, and pay its approved invoices by bank file."
      onClose={onClose} onSave={save} saving={saving} saveLabel={provider ? 'Save provider' : 'Add provider'}
    >
      <div className="space-y-3.5">
        {provider && !inUse(provider) && (
          <p className="rounded-lg bg-slate-100 px-3 py-2 text-[13px] text-slate-700">
            Not in use. It is not offered for new purchase orders; use it again from the provider list.
          </p>
        )}
        <Field label="Provider name" required>
          {(id) => (
            <input
              id={id} className="input-field" value={form.name}
              onChange={e => set('name', e.target.value)}
              placeholder="Northside Allied Health"
            />
          )}
        </Field>
        <Field label="ABN">
          {(id) => (
            <input
              id={id} className="input-field" value={form.abn}
              onChange={e => set('abn', e.target.value)}
              placeholder="11 222 333 444"
            />
          )}
        </Field>
        <Field label="Contact name">
          {(id) => <input id={id} className="input-field" value={form.contactName} onChange={e => set('contactName', e.target.value)} />}
        </Field>
        <FormRow cols={2}>
          <Field label="Email">
            {(id) => <input id={id} type="email" className="input-field" value={form.email} onChange={e => set('email', e.target.value)} />}
          </Field>
          <Field label="Phone">
            {(id) => <input id={id} className="input-field" value={form.phone} onChange={e => set('phone', e.target.value)} />}
          </Field>
        </FormRow>
        <FormRow cols={2}>
          <Field label="Worker screening attested on" hint="Their written statement that every worker they send has current worker screening.">
            {(id) => <input id={id} type="date" className="input-field" value={form.screeningAttestedAt} onChange={e => set('screeningAttestedAt', e.target.value)} />}
          </Field>
          <Field label="Attestation good until">
            {(id) => <input id={id} type="date" className="input-field" value={form.screeningExpires} onChange={e => set('screeningExpires', e.target.value)} />}
          </Field>
        </FormRow>
        <div className="pt-2 border-t border-slate-100">
          <p className="text-[13px] font-semibold text-slate-800">Bank details for payments</p>
          <p className="text-[12px] text-slate-500 mt-0.5">Needed to pay this provider's approved invoices by bank file.</p>
        </div>
        <FormRow cols={2}>
          <Field label="BSB">
            {(id) => <input id={id} className="input-field" inputMode="numeric" value={form.bsb} onChange={e => set('bsb', e.target.value)} placeholder="062-000" />}
          </Field>
          <Field label="Account number">
            {(id) => <input id={id} className="input-field" inputMode="numeric" value={form.accountNumber} onChange={e => set('accountNumber', e.target.value)} placeholder="12345678" />}
          </Field>
        </FormRow>
        <FormRow cols={2}>
          <Field label="Account name">
            {(id) => <input id={id} className="input-field" value={form.accountName} onChange={e => set('accountName', e.target.value)} />}
          </Field>
          <Field label="Remittance email" hint="Where payment advice goes. Leave blank to use the email above.">
            {(id) => <input id={id} type="email" className="input-field" value={form.remittanceEmail} onChange={e => set('remittanceEmail', e.target.value)} />}
          </Field>
        </FormRow>
      </div>
    </FormDrawer>
  );
}

// The item a purchase order buys, with the details Services Australia needs, so the claim line is
// complete when the supplier's invoice is approved.
const PO_ATHM = new Set(['SERV-0061', 'SERV-0062', 'SERV-0063', 'SERV-0064', 'SERV-0065', 'SERV-0066', 'SERV-0067', 'SERV-0068']);
const PO_PRODUCT = new Set(['SERV-0061', 'SERV-0062', 'SERV-0063', 'SERV-0064', 'SERV-0065']);
const PO_PROFESSIONAL = new Set(['SERV-0066', 'SERV-0068']);
function POItemFields({ value, onChange }) {
  const [ref, setRef] = useState({ services: [], wraparounds: [], items: [], professionals: [] });
  const v = value || {};
  const set = (k, x) => onChange({ ...v, [k]: x });
  useEffect(() => { api.get('/aged-care/claim-items/reference').then((r) => setRef((p) => ({ ...p, services: r.data?.services || [], wraparounds: r.data?.wraparounds || [] }))).catch(() => {}); }, []);
  useEffect(() => {
    if (!v.service_id) return;
    api.get('/aged-care/claim-items/reference', { params: { service: v.service_id } })
      .then((r) => setRef((p) => ({ ...p, items: r.data?.items || [], professionals: r.data?.professionals || [] }))).catch(() => {});
  }, [v.service_id]);
  const athm = PO_ATHM.has(v.service_id || '');
  const code = ref.items.find((i) => i.c === v.item_code);
  const wrap = ref.wraparounds.find((w) => w.c === v.wraparound_code);
  const prof = ref.professionals.find((h) => h.c === v.health_prof_code);
  return (
    <div className="space-y-3 rounded-lg border border-slate-200 p-3">
      <Field label="What it is for" hint="The official service it is claimed under.">
        {(id) => (
          <select id={id} className="input-field" value={v.service_id || ''} onChange={(e) => onChange({ service_id: e.target.value, item_or_wraparound: 'ITEM', purchase_method: 'PURCHASED', quantity: 1 })}>
            <option value="">Choose…</option>
            {ref.services.map((s) => <option key={s.id} value={s.id}>{s.type}: {s.name}</option>)}
          </select>
        )}
      </Field>
      {v.service_id && athm && (
        <div className="flex gap-2 text-[13px]">
          {[['ITEM', 'The item'], ['WRAPAROUND', 'A service around it']].map(([k, l]) => (
            <button key={k} type="button" onClick={() => set('item_or_wraparound', k)} aria-pressed={v.item_or_wraparound === k}
              className={`px-3 py-1.5 rounded-lg border ${v.item_or_wraparound === k ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-slate-200 text-slate-600'}`}>{l}</button>
          ))}
        </div>
      )}
      {v.service_id && (!athm || v.item_or_wraparound === 'ITEM') && !PO_PROFESSIONAL.has(v.service_id) && (
        <Field label="Item">
          {(id) => (
            <select id={id} className="input-field" value={v.item_code || ''} onChange={(e) => set('item_code', e.target.value)}>
              <option value="">Choose…</option>
              {ref.items.map((i) => <option key={i.c} value={i.c}>{i.n}</option>)}
            </select>
          )}
        </Field>
      )}
      {code?.o === 1 && <Field label="Describe the item">{(id) => <input id={id} className="input-field" value={v.item_description || ''} onChange={(e) => set('item_description', e.target.value)} />}</Field>}
      {athm && v.item_or_wraparound === 'WRAPAROUND' && (
        <Field label="Service around the item">
          {(id) => (
            <select id={id} className="input-field" value={v.wraparound_code || ''} onChange={(e) => set('wraparound_code', e.target.value)}>
              <option value="">Choose…</option>
              {ref.wraparounds.map((w) => <option key={w.c} value={w.c}>{w.n}</option>)}
            </select>
          )}
        </Field>
      )}
      {wrap?.o === 1 && <Field label="Describe it">{(id) => <input id={id} className="input-field" value={v.wraparound_description || ''} onChange={(e) => set('wraparound_description', e.target.value)} />}</Field>}
      {PO_PROFESSIONAL.has(v.service_id || '') && (
        <Field label="Health professional">
          {(id) => (
            <select id={id} className="input-field" value={v.health_prof_code || ''} onChange={(e) => set('health_prof_code', e.target.value)}>
              <option value="">Choose…</option>
              {ref.professionals.map((h) => <option key={h.c} value={h.c}>{h.n}</option>)}
            </select>
          )}
        </Field>
      )}
      {prof?.o === 1 && <Field label="Describe the professional">{(id) => <input id={id} className="input-field" value={v.health_prof_other || ''} onChange={(e) => set('health_prof_other', e.target.value)} />}</Field>}
      {PO_PRODUCT.has(v.service_id || '') && v.item_or_wraparound === 'ITEM' && (
        <FormRow cols={2}>
          <Field label="Bought or loaned">
            {(id) => (
              <select id={id} className="input-field" value={v.purchase_method || 'PURCHASED'} onChange={(e) => set('purchase_method', e.target.value)}>
                <option value="PURCHASED">Bought</option>
                <option value="LOANED">Loaned</option>
              </select>
            )}
          </Field>
          {v.purchase_method === 'LOANED' && (
            <Field label="Lending organisation and its invoice number">
              {(id) => (
                <div className="flex gap-2">
                  <input id={id} className="input-field" placeholder="Organisation" value={v.loaned_by_org || ''} onChange={(e) => set('loaned_by_org', e.target.value)} />
                  <input aria-label="Invoice number" className="input-field" placeholder="Invoice no." value={v.loaned_by_invoice || ''} onChange={(e) => set('loaned_by_invoice', e.target.value)} />
                </div>
              )}
            </Field>
          )}
        </FormRow>
      )}
      {athm && (
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-[13px] text-slate-700">
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!v.prescribed} onChange={(e) => set('prescribed', e.target.checked)} /> Prescribed</label>
          {v.service_id === 'SERV-0067' && <label className="flex items-center gap-2"><input type="checkbox" checked={!!v.first_payment} onChange={(e) => set('first_payment', e.target.checked)} /> First payment for this home modification</label>}
        </div>
      )}
      {v.service_id && (
        <FormRow cols={2}>
          <Field label="Quantity">{(id) => <input id={id} type="number" min="1" className="input-field" value={v.quantity ?? 1} onChange={(e) => set('quantity', e.target.value)} />}</Field>
          <Field label="Price each ($)" hint="Blank uses the supplier's invoice total.">{(id) => <input id={id} type="number" min="0" step="0.01" className="input-field" value={v.unit_price ?? ''} onChange={(e) => set('unit_price', e.target.value)} />}</Field>
        </FormRow>
      )}
    </div>
  );
}

// Raise a purchase order, or edit one (`order` set). An order stays with the participant it was
// raised for, and the item it buys for a claim is fixed when it is raised, so those two are not
// offered for change; everything else, including closing or cancelling it, is.
function POModal({ order, providers, clients, clientsError, onRetryClients, onClose, onSaved }) {
  const editing = !!order;
  const [saving, setSaving] = useState(false);
  const [withItem, setWithItem] = useState(false);
  const [item, setItem] = useState({});
  const [form, setForm] = useState({
    clientId: order ? String(order.client_id || '') : '',
    providerId: order ? String(order.provider_id || '') : '',
    poNumber: order ? (order.po_number || order.poNumber || '') : '',
    serviceType: order ? (order.service_type || order.serviceType || '') : '',
    allocated: order ? String(allocOf(order)) : '',
    effectiveFrom: order ? ymd(order.effective_from || order.effectiveFrom) : '',
    effectiveTo: order ? ymd(order.effective_to || order.effectiveTo) : '',
    status: order ? poStatus(order) : 'active',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Only providers in use are offered; an order already with a stopped provider still shows it.
  const providerOptions = providers.filter(p => inUse(p) || String(p.id) === form.providerId);
  // The participant on an order being edited is shown even if the list did not load.
  const clientOptions = editing && form.clientId && !clients.some(c => String(c.id) === form.clientId)
    ? [{ id: form.clientId, name: order.client_name || 'The participant on this order' }, ...clients]
    : clients;

  const save = async () => {
    if (!form.clientId) return toast.error('Choose the participant this order is for');
    if (!form.providerId) return toast.error('Choose the provider this order is with');
    if (!form.poNumber.trim()) return toast.error('Enter the purchase order number');
    if (!form.allocated || Number(form.allocated) <= 0) return toast.error('Enter the amount this order authorises');
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/aged-care/purchase-orders/${order.id}`, {
          poNumber: form.poNumber.trim(),
          serviceType: form.serviceType,
          allocated: Number(form.allocated),
          effectiveFrom: form.effectiveFrom || null,
          effectiveTo: form.effectiveTo || null,
          status: form.status,
          providerId: form.providerId,
        });
        toast.success('Purchase order saved');
      } else {
        await api.post('/aged-care/purchase-orders', {
          ...form,
          allocated: Number(form.allocated),
          claimItem: withItem && item.service_id ? { ...item, quantity: Number(item.quantity) || 1, unit_price: item.unit_price === '' || item.unit_price == null ? null : Number(item.unit_price) } : null,
        });
        toast.success(withItem ? 'Purchase order created. When its invoice is approved, the item goes on the claim.' : 'Purchase order created');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.message || (editing ? 'Could not save the purchase order' : 'Could not create the purchase order'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDrawer
      icon={FileText}
      title={editing ? 'Edit purchase order' : 'Raise a purchase order'}
      purpose="The order caps what this provider can bill for this participant, and for how long."
      onClose={onClose} onSave={save} saving={saving} saveLabel={editing ? 'Save changes' : 'Create purchase order'}
    >
      <div className="space-y-3.5">
        <FormRow cols={2}>
          <Field
            label="Participant" required
            error={!editing && clientsError ? clientsError : undefined}
            hint={editing ? 'An order stays with the participant it was raised for. Raise a new one for someone else.' : undefined}
            action={!editing && clientsError ? (
              <button type="button" onClick={onRetryClients} className="text-[12px] text-primary-700 hover:underline">Try again</button>
            ) : null}
          >
            {(id) => (
              <select id={id} className="input-field" value={form.clientId} disabled={editing} onChange={e => set('clientId', e.target.value)}>
                <option value="">Choose a participant</option>
                {clientOptions.map(c => (
                  <option key={c.id} value={String(c.id)}>
                    {c.name || [c.first_name, c.last_name].filter(Boolean).join(' ') || 'Participant'}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Provider" required>
            {(id) => (
              <select id={id} className="input-field" value={form.providerId} onChange={e => set('providerId', e.target.value)}>
                <option value="">Choose a provider</option>
                {providerOptions.map(p => <option key={p.id} value={String(p.id)}>{p.name}{inUse(p) ? '' : ' (not in use)'}</option>)}
              </select>
            )}
          </Field>
        </FormRow>
        <FormRow cols={2}>
          <Field label="Purchase order number" required>
            {(id) => (
              <input
                id={id} className="input-field" value={form.poNumber}
                onChange={e => set('poNumber', e.target.value)}
                placeholder="PO-2026-0001"
              />
            )}
          </Field>
          <Field label="Service type">
            {(id) => (
              <>
                <input
                  id={id} list="ac-po-service-types" className="input-field" value={form.serviceType}
                  onChange={e => set('serviceType', e.target.value)}
                  placeholder="Allied Health"
                />
                <datalist id="ac-po-service-types">
                  {SERVICE_TYPES.map(t => <option key={t} value={t} />)}
                </datalist>
              </>
            )}
          </Field>
        </FormRow>
        {!editing && (
          <>
            <label className="flex items-start gap-2 text-[13px] text-slate-700 cursor-pointer">
              <input type="checkbox" className="mt-0.5" checked={withItem} onChange={(e) => setWithItem(e.target.checked)} />
              <span>It buys an item for a Support at Home claim<span className="block text-[12px] text-slate-500">Assistive technology, home modifications or a home support item: record its claim details now, and the approved invoice becomes the claim line.</span></span>
            </label>
            {withItem && <POItemFields value={item} onChange={setItem} />}
          </>
        )}
        {editing && order.claim_item && (
          <p className="rounded-lg bg-slate-50 px-3 py-2 text-[13px] text-slate-600">
            This order buys an item for a Support at Home claim. The item details stay as they were when the order was raised.
          </p>
        )}
        <Field label="Amount authorised" required hint="The provider cannot bill more than this against the order.">
          {(id) => (
            <input
              id={id} type="number" min="0" step="0.01" className="input-field" value={form.allocated}
              onChange={e => set('allocated', e.target.value)}
              placeholder="0.00"
            />
          )}
        </Field>
        <FormRow cols={2}>
          <Field label="Effective from">
            {(id) => <input id={id} type="date" className="input-field" value={form.effectiveFrom} onChange={e => set('effectiveFrom', e.target.value)} />}
          </Field>
          <Field label="Effective to">
            {(id) => <input id={id} type="date" className="input-field" value={form.effectiveTo} onChange={e => set('effectiveTo', e.target.value)} />}
          </Field>
        </FormRow>
        {editing && (
          <Field label="Status" hint="A closed or cancelled order no longer matches new invoices. Invoices already approved against it are not changed.">
            {(id) => (
              <select id={id} className="input-field" value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="active">Open</option>
                <option value="closed">Closed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            )}
          </Field>
        )}
      </div>
    </FormDrawer>
  );
}

export default function AgedCareProviders() {
  const confirm = useConfirm();
  const [providers, setProviders] = useState([]);
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [providersError, setProvidersError] = useState(null);
  const [ordersError, setOrdersError] = useState(null);
  const [clientsError, setClientsError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  // false, true (add) or the provider / order being edited.
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [showPOModal, setShowPOModal] = useState(false);

  const loadProviders = useCallback(async () => {
    setLoadingProviders(true);
    try {
      const r = await api.get('/aged-care/providers');
      setProviders(Array.isArray(r.data) ? r.data : (r.data?.providers || []));
      setProvidersError(null);
    } catch (e) {
      setProvidersError(e.response?.data?.message || 'Could not load the providers');
    } finally {
      setLoadingProviders(false);
    }
  }, []);

  const loadOrders = useCallback(async () => {
    setLoadingOrders(true);
    try {
      const r = await api.get('/aged-care/purchase-orders');
      setOrders(Array.isArray(r.data) ? r.data : (r.data?.purchaseOrders || []));
      setOrdersError(null);
    } catch (e) {
      setOrdersError(e.response?.data?.message || 'Could not load the purchase orders');
    } finally {
      setLoadingOrders(false);
    }
  }, []);

  const loadClients = useCallback(async () => {
    setClientsError(null);
    try {
      const r = await api.get('/aged-care/clients');
      setClients(Array.isArray(r.data) ? r.data : (r.data?.clients || []));
    } catch (e) {
      setClientsError(e.response?.data?.message || 'Could not load the participants');
    }
  }, []);

  useEffect(() => {
    loadProviders();
    loadOrders();
    loadClients();
  }, [loadProviders, loadOrders, loadClients]);

  // Figures count open orders only: a closed or cancelled order's balance can no longer be billed.
  const openPOs = orders.filter(isOpenPO);
  const totalAllocated = openPOs.reduce((s, o) => s + allocOf(o), 0);
  const totalUtilised = openPOs.reduce((s, o) => s + usedOf(o), 0);
  const totalBalance = openPOs.reduce((s, o) => s + balanceOf(o), 0);
  const overspent = orders.filter(o => usedOf(o) > allocOf(o)).length;
  const providersInUse = providers.filter(inUse);

  const providerName = (o) => {
    if (o.provider_name) return o.provider_name;
    const p = providers.find(p => String(p.id) === String(o.provider_id));
    return p ? p.name : '—';
  };
  const clientName = (o) => {
    if (o.client_name) return o.client_name;
    const c = clients.find(c => String(c.id) === String(o.client_id));
    if (!c) return '—';
    return c.name || [c.first_name, c.last_name].filter(Boolean).join(' ') || '—';
  };

  const openPOModal = () => {
    if (providers.length === 0) return toast.error('Add a provider first, then raise the order against it');
    if (providersInUse.length === 0) return toast.error('No provider is in use. Use one again, or add a provider, before raising an order.');
    setShowPOModal(true);
  };

  const setProviderActive = async (p, active) => {
    if (!active) {
      const ok = await confirm(
        `${p.name} is no longer offered when you raise a purchase order. Its existing purchase orders and invoices are not changed, and you can use it again at any time.`,
        { title: `Stop using ${p.name}?`, confirmLabel: 'Stop using', danger: true },
      );
      if (!ok) return;
    }
    setBusyId(p.id);
    try {
      await api.post(`/aged-care/providers/${p.id}/active`, { active });
      toast.success(active ? `${p.name} is in use again` : `${p.name} is no longer in use`);
      loadProviders();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not update the provider');
    } finally {
      setBusyId(null);
    }
  };

  // A provider no longer in use is greyed across the row, and says so beside its name.
  const dim = (p) => (inUse(p) ? '' : 'text-slate-400');

  const providerColumns = [
    {
      key: 'name', header: 'Provider', sortable: true, truncate: true,
      render: (p) => (
        <span className="flex items-center gap-2 min-w-0">
          <span className={`truncate font-medium ${inUse(p) ? 'text-slate-800' : 'text-slate-400'}`}>{p.name || '—'}</span>
          {!inUse(p) && <span className="badge badge-gray shrink-0">Not in use</span>}
        </span>
      ),
    },
    { key: 'abn', header: 'ABN', width: 160, render: (p) => <span className={`tabular-nums ${dim(p)}`}>{p.abn || '—'}</span> },
    { key: 'contact_name', header: 'Contact', sortable: true, truncate: true, render: (p) => <span className={dim(p)}>{p.contact_name || p.contactName || '—'}</span> },
    { key: 'email', header: 'Email', truncate: true, render: (p) => <span className={dim(p)}>{p.email || '—'}</span> },
    { key: 'phone', header: 'Phone', width: 150, render: (p) => <span className={dim(p)}>{p.phone || '—'}</span> },
    { key: 'bank', header: 'Bank details', width: 130, render: (p) => (p.bsb && p.account_number
      ? <span className={`tabular-nums ${inUse(p) ? 'text-slate-700' : 'text-slate-400'}`}>{p.bsb}</span>
      : <span className={inUse(p) ? 'text-amber-700' : 'text-slate-400'}>Not set</span>) },
  ];

  const orderColumns = [
    {
      key: 'po_number', header: 'Order', width: 150, sortable: true, truncate: true,
      sortValue: (o) => o.po_number || o.poNumber || null,
      render: (o) => (
        <span className="block min-w-0">
          <span className="block truncate font-medium text-slate-800">{o.po_number || o.poNumber || '—'}</span>
          {schemeLabel(o.scheme) && <span className="block text-[11px] text-slate-500 truncate">{schemeLabel(o.scheme)}</span>}
        </span>
      ),
    },
    { key: 'client_name', header: 'Participant', width: 170, sortable: true, truncate: true, sortValue: clientName, render: clientName },
    // The service ordered sits under the provider, so neither is cut to a few letters.
    {
      key: 'provider_name', header: 'Provider and service', width: 210, sortable: true, truncate: true, sortValue: providerName,
      render: (o) => (
        <span className="block min-w-0">
          <span className="block truncate">{providerName(o)}</span>
          <span className="block truncate text-[11px] text-slate-500">{o.service_type || o.serviceType || 'Service not named'}</span>
        </span>
      ),
    },
    { key: 'allocated', header: 'Allocated', money: true, sortable: true, sortValue: allocOf, render: (o) => <Currency value={allocOf(o)} /> },
    { key: 'utilised', header: 'Utilised', money: true, sortable: true, sortValue: usedOf, render: (o) => <Currency value={usedOf(o)} /> },
    {
      key: 'balance', header: 'Balance', money: true, sortable: true, sortValue: balanceOf,
      render: (o) => <Currency value={balanceOf(o)} className="font-medium text-slate-900" />,
    },
    {
      key: 'effective_from', header: 'Effective', width: 130, sortable: true,
      sortValue: (o) => o.effective_from || o.effectiveFrom || null,
      render: (o) => {
        const from = o.effective_from || o.effectiveFrom;
        const to = o.effective_to || o.effectiveTo;
        if (!from && !to) return <span className="text-slate-300">—</span>;
        return (
          <span className="block tabular-nums text-slate-500 whitespace-nowrap leading-snug">
            <span className="block">{from ? fmtDate(from) : 'No start date'}</span>
            <span className="block text-[11px]">to {to ? fmtDate(to) : 'no end date'}</span>
          </span>
        );
      },
    },
    {
      key: 'status', header: 'Status', width: 110, sortable: true,
      sortValue: (o) => PO_STATUS[poStatus(o)],
      render: (o) => <AcStatus status={poStatus(o)} label={PO_STATUS[poStatus(o)]} />,
    },
  ];

  return (
    <div>
      <PageHead icon={Building2}
        title="Providers and purchase orders"
        purpose="Services you broker out are delivered by third-party providers and paid against a purchase order. The order caps what a provider can bill for one participant, over a set period."
        actions={(
          <>
            <button type="button" onClick={() => setShowProviderModal(true)} className="btn-secondary">
              <Plus size={15} /> Add a provider
            </button>
            <button type="button" onClick={openPOModal} className="btn-primary">
              <Plus size={15} /> Raise a purchase order
            </button>
          </>
        )}
      />

      {/* Hidden while either list failed to load: zeros would read as "none", not "unknown". */}
      {!providersError && !ordersError && (
        <FigureStrip>
          <Figure label="Providers in use" value={providersInUse.length} sub={providersInUse.length !== providers.length ? `${providers.length} in total` : undefined} />
          <Figure label="Open purchase orders" value={openPOs.length} sub={orders.length ? `${orders.length} in total` : undefined} />
          <Figure label="Allocated on open orders" value={money(totalAllocated)} />
          <Figure label="Balance remaining" value={money(totalBalance)} tone={overspent ? 'red' : 'slate'} />
        </FigureStrip>
      )}

      <div className="space-y-5">
        <Register
          title="Providers"
          count={providers.length}
          countLabel={providers.length === 1 ? 'provider' : 'providers'}
          columns={providerColumns}
          rows={providers}
          rowKey="id"
          onRowClick={(p) => setShowProviderModal(p)}
          loading={loadingProviders}
          error={providersError}
          onRetry={loadProviders}
          rowActions={(p) => (
            <span className="flex items-center gap-1" onClick={stop} onKeyDown={stop}>
              {inUse(p) ? (
                <button type="button" onClick={() => setProviderActive(p, false)} disabled={busyId === p.id} className="btn-ghost btn-sm text-slate-500" aria-label={`Stop using ${p.name}`}>
                  <Ban size={13} /> Stop using
                </button>
              ) : (
                <button type="button" onClick={() => setProviderActive(p, true)} disabled={busyId === p.id} className="btn-ghost btn-sm" aria-label={`Use ${p.name} again`}>
                  <RotateCcw size={13} /> Use again
                </button>
              )}
            </span>
          )}
          empty={{
            icon: Building2,
            title: 'No providers yet',
            hint: 'Add the third-party providers you broker services from, then raise a purchase order against one.',
            action: (
              <button type="button" onClick={() => setShowProviderModal(true)} className="btn-primary btn-sm">
                <Plus size={14} /> Add a provider
              </button>
            ),
          }}
        />

        <Register
          title="Purchase orders"
          count={orders.length}
          countLabel={orders.length === 1 ? 'order' : 'orders'}
          columns={orderColumns}
          rows={orders}
          rowKey="id"
          onRowClick={(o) => setShowPOModal(o)}
          loading={loadingOrders}
          error={ordersError}
          onRetry={loadOrders}
          rowTone={poTone}
          empty={{
            icon: FileText,
            title: 'No purchase orders yet',
            hint: 'Raise an order to authorise a provider to bill against one participant’s budget, up to a set amount.',
            action: (
              <button type="button" onClick={openPOModal} className="btn-primary btn-sm">
                <Plus size={14} /> Raise a purchase order
              </button>
            ),
          }}
          footer={`Open orders: allocated ${money(totalAllocated)}, utilised ${money(totalUtilised)}, balance ${money(totalBalance)}.${overspent ? ` ${overspent} order${overspent === 1 ? ' has' : 's have'} been billed past the amount authorised.` : ''} Open an order to change it, close it or cancel it.`}
        />

        <Panel title="How a supplier invoice is matched to a purchase order" icon={Info} tone="muted" bodyClass="px-4 py-3">
          <p className="text-[13px] text-slate-600 leading-relaxed">
            Matching uses the provider name, the amount and the date. An invoice matches when an open purchase order
            exists with a provider whose name matches the supplier on the invoice, the invoice total fits inside the
            order&apos;s remaining balance, and the invoice date falls within the order&apos;s effective dates. Anything
            else is held on the supplier invoice queue for someone to review.
          </p>
        </Panel>
      </div>

      {showProviderModal && (
        <ProviderModal
          provider={showProviderModal === true ? null : showProviderModal}
          onClose={() => setShowProviderModal(false)}
          onSaved={() => { setShowProviderModal(false); loadProviders(); }}
        />
      )}
      {showPOModal && (
        <POModal
          order={showPOModal === true ? null : showPOModal}
          providers={providers}
          clients={clients}
          clientsError={clientsError}
          onRetryClients={loadClients}
          onClose={() => setShowPOModal(false)}
          onSaved={() => { setShowPOModal(false); loadOrders(); }}
        />
      )}
    </div>
  );
}
